import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Compass, ArrowLeft, Home, ShoppingBag, Wrench, Search } from 'lucide-react';

const NotFound = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-[75vh] flex items-center justify-center bg-gray-50 px-4 py-16 sm:px-6 lg:px-8">
      <Helmet>
        <title>Página no encontrada (404) | PROOMTB & ROAD</title>
        <meta name="robots" content="noindex, follow" />
        <meta
          name="description"
          content="La página que buscas no está disponible o ha cambiado de dirección en PROOMTB & ROAD."
        />
      </Helmet>

      <div className="max-w-2xl w-full text-center">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-50 border border-red-200 text-red-600 text-sm font-semibold tracking-wide uppercase">
            <Compass className="w-4 h-4 animate-spin-slow" />
            Error 404 — Fuera de ruta
          </div>

          {/* Big number */}
          <h1 className="text-7xl sm:text-9xl font-black italic text-gray-900 tracking-tight">
            4<span className="text-red-500">0</span>4
          </h1>

          {/* Title & Description */}
          <div className="space-y-3">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              ¡Parece que te desviaste del camino!
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-lg mx-auto">
              La página o producto que estás buscando no existe, fue movida o el enlace es incorrecto. No te preocupes, podemos ayudarte a retomar el sendero.
            </p>
          </div>

          {/* Primary Buttons */}
          <div className="pt-2 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold hover:bg-gray-100 transition-colors shadow-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Regresar
            </button>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-black text-white font-semibold hover:bg-gray-800 transition-colors shadow-md"
            >
              <Home className="w-4 h-4" />
              Ir al Inicio
            </Link>
          </div>

          {/* Quick Links Section */}
          <div className="pt-8 mt-8 border-t border-gray-200">
            <p className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4">
              Secciones recomendadas
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link
                to="/catalogo"
                className="group p-4 bg-white rounded-xl border border-gray-200 hover:border-black transition-all shadow-sm hover:shadow text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center mb-2 group-hover:bg-black group-hover:text-white transition-colors">
                  <ShoppingBag className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Catálogo de Bicicletas</h3>
                <p className="text-xs text-gray-500 mt-0.5">Explora e-bikes, MTB y ruta</p>
              </Link>

              <Link
                to="/accesorios"
                className="group p-4 bg-white rounded-xl border border-gray-200 hover:border-black transition-all shadow-sm hover:shadow text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center mb-2 group-hover:bg-black group-hover:text-white transition-colors">
                  <Search className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Accesorios y Piezas</h3>
                <p className="text-xs text-gray-500 mt-0.5">Equipamiento e indumentaria</p>
              </Link>

              <Link
                to="/agendar-mantenimiento"
                className="group p-4 bg-white rounded-xl border border-gray-200 hover:border-black transition-all shadow-sm hover:shadow text-left"
              >
                <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center mb-2 group-hover:bg-black group-hover:text-white transition-colors">
                  <Wrench className="w-5 h-5" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">Taller y Servicio</h3>
                <p className="text-xs text-gray-500 mt-0.5">Mantenimiento certificado</p>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default NotFound;
